import type { Messages } from "./messages.ts";

export const en: Messages = {
  app: {
    name: "Boomap",
    tagline: "The candy houses in your neighbourhood, on Halloween night.",
    description: "Boomap: the collaborative map of houses handing out candy on Halloween night.",
    mapLabel: "Map of houses handing out candy",
  },

  treats: {
    available: {
      label: "Candy",
      hint: "This house is handing out candy tonight.",
    },
    exhausted: {
      label: "Out of candy",
      hint: "They have run out — no point ringing.",
    },
    none: {
      label: "No candy",
      hint: "Nothing to collect here.",
    },
  },

  legend: {
    label: "Map legend",
    title: "Legend",
    open: "Show the legend and legal notice",
  },

  add: {
    label: "Add a house",
  },

  placement: {
    regionLabel: "Placing a marker",
    title: "Line the crosshair up with the house.",
    hint: "Drag the map — the crosshair stays centred.",
    cancel: "Cancel",
    confirm: "Continue",
  },

  declare: {
    title: "This house…",
    treatsLegend: "Candy tonight",
    noteLabel: "Optional detail",
    notePlaceholder: "Ring twice, gate open until 9pm…",
    location: ({ latitude, longitude }) =>
      `${latitude}, ${longitude} — the marker disappears by itself the day after Halloween.`,
    cancelLabel: "Cancel",
    cancel: "Cancel",
    submit: "Drop the marker",
    submitting: "Saving…",
  },

  spot: {
    closeLabel: "Close",
    declaredAt: (time) => `Reported at ${time}`,
    updated: (relative) => `Updated ${relative}`,
    justUpdated: "Updated just now",
    // English pluralises zero.
    reports: (count) => `${count.toString()} report${count === 1 ? "" : "s"}`,
    report: "Report a mistake",
    reporting: "Sending…",
    close: "Close",
    retract: "Delete my marker",
    retracting: "Deleting…",
    takeDownLink: "This is my home, remove it from the map",
    takeDown: {
      title: "Remove this home?",
      effect:
        "The marker disappears immediately, and this location cannot be re-added this season.",
      attestation: "By confirming, you declare that you occupy this home.",
      cancel: "Cancel",
      confirm: "Remove",
      submitting: "Removing…",
    },
  },

  search: {
    label: "Search for a place",
    placeholder: "Town, street, address…",
    clear: "Clear the search",
    empty: "No place matches.",
    tooShort: "One more character…",
  },

  banner: {
    zoomedOut: "Zoom in or search for a town to see the houses.",
    truncated: "Too many houses around here — zoom in to see them all.",
  },

  toast: {
    added: "Marker added. Thank you!",
    alreadyThere: "This house is already on the map.",
    removed: "Thanks — the marker has been taken off the map.",
    reported: "Report recorded.",
    alreadyReported: "You have already reported this house.",
    takenDown: "The home has been removed from the map.",
    retracted: "Your marker has been deleted.",
  },

  locate: {
    label: "Find my location",
    unsupported: "This browser cannot locate you.",
    denied: "Location denied or unavailable.",
  },

  basemap: {
    night: "night",
    day: "day",
    switchTo: (name) => `Switch to the ${name} basemap`,
  },

  localeSwitch: (name) => `Show the site in ${name}`,

  legal: {
    link: "Privacy",
    title: "Privacy & legal notice",
    close: "Close",
    sections: [
      {
        heading: "A community-run map",
        body:
          "Every marker is placed by a visitor. Nothing is verified, by us or by anyone " +
          "else: a listed house may not be handing out candy, and a house that is may be " +
          "missing from the map. Treat this as a tip from neighbours, not as official " +
          "information.",
      },
      {
        heading: "Children remain their parents' responsibility",
        body:
          "This map is an aid, not supervision. Parents and legal guardians remain solely " +
          "responsible for supervising their minor children, for choosing which houses " +
          "they ring, for the route taken and for what is collected. We know neither the " +
          "residents, nor the places, nor the treats.",
      },
      {
        heading: "No cookies, no analytics",
        body:
          "This site sets no cookies and uses no analytics or advertising tools. That is " +
          "why you are not shown a consent banner: there would be nothing to consent to.",
      },
      {
        heading: "What is kept in your browser",
        body:
          "Two display preferences only, written when you click the matching button: the " +
          "language and the day/night basemap. They stay in your browser and are never " +
          "sent to the server.",
      },
      {
        heading: "What is kept on the server",
        body:
          "For each marker: a point on the map, the candy state, whether the house is " +
          "decorated, your optional note, and dates. Your IP address is read and turned " +
          "into an irreversible fingerprint, salted with a secret and with the current " +
          "date; it only serves to limit abuse and expires every night. Your IP address is " +
          "never kept in the clear, neither in the database nor in the logs.",
      },
      {
        heading: "Your note is public",
        body:
          "The free text you attach to a marker is visible to everyone. Do not write " +
          "names, phone numbers, or anything about someone else.",
      },
      {
        heading: "Basemaps",
        body:
          "Map tiles are loaded by your browser from tiles.versatiles.org, which passes " +
          "your IP address to that service, as with any image loaded from another site. " +
          "Place search goes through our own server: your browser never contacts the " +
          "gazetteer directly.",
      },
      {
        heading: "Location",
        body:
          "Your position is only requested if you press the locate button, and your " +
          "browser asks for your permission. It is used to centre the map, and is neither " +
          "stored nor sent to anyone.",
      },
      {
        heading: "How long data is kept",
        body:
          "Everything expires automatically on 1 November at 6am. No data is carried over " +
          "from one year to the next.",
      },
      {
        heading: "Is this your home?",
        body:
          "Open the marker and use “This is my home, remove it from the map”. Removal is " +
          "immediate and the location cannot be re-added for the season. You do not need " +
          "anyone's approval.",
      },
    ],
    identity: {
      heading: "Legal notice",
      publisher: "Published by a private individual on a non-professional basis.",
      contactLabel: "Contact",
    },
    updated: (date) => `Last updated: ${date}`,
  },

  errors: {
    invalid_value: "Those details are not valid.",
    spot_not_found: "This marker no longer exists.",
    spot_already_declared_nearby: "A marker already exists a few metres away.",
    not_the_author:
      "This marker was added from another device, or yesterday — it can no longer be removed this way.",
    location_taken_down:
      "The occupant of this home asked to be removed from the map. Please respect that.",
    takedown_quota_exceeded: "You have reached your removal limit. Try again tomorrow.",
    contribution_quota_exceeded: "You have reached your reporting quota. Try again later.",
    place_search_unavailable: "Place search is temporarily unavailable.",
    not_found: "Resource not found.",
    internal_error: "Something unexpected went wrong.",
    network: "A network error occurred. Try again in a moment.",
    spotsUnavailable: "Unable to load the markers right now.",
  },
};
